import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from 'react';

import { every, some } from 'lodash';

import { Box, Card, CardContent } from '@mui/material';

import { parseId } from 'src/utils/parsing';

import { useSearchParams } from 'react-router-dom';

import {
  RowIdentifier,
  Scenario,
  ScenarioEditState,
  ScenarioArchiveFlag,
  DefaultModelFiles,
  Submission,
  SubmissionStatus,
  ModelInstance,
} from '../../../types/models';
import {
  isModelMember,
  isModelOwner,
  isModelRestricted,
} from '../../../utils/profile';
import { isTrinityUser } from '../../../utils/user';

import { ProfileProps, withProfile } from '../../shared/AuthController';

import Loader, { LoaderInfo } from '../../shared/Loader';

import APICacheContext from '../../shared/APICacheContext';

import APICache from '../../../services/APICache';

import DownloadForecast from '../DownloadForecast';
import ScenarioDetailsForm from '../ScenarioDetailsForm';
import UploadForecast from '../UploadForecast';

import CenteredSpinner from '../../shared/CenteredSpinner';

import EditAssumptions from '../EditAssumptions';

import {
  getScenarioLinkTagsListPath,
  loadAllScenarioLinkTags,
} from '../ManageScenarioTags';

import ScenarioVersions from './ScenarioVersions';
import ScenarioTagAssignment from './ScenarioTagAssignment';

interface Props extends ProfileProps {
  modelId: number;
  instanceId: number;
  scenarioId: number;
  refreshScenarios: () => void;
}

const ScenarioDetails: FunctionComponent<Props> = ({
  scenarioId,
  profile,
  modelId,
  instanceId,
  refreshScenarios,
}) => {
  const scenarioDetailsContext = useContext(APICacheContext);

  const [allGroupsLocked, setAllGroupsLocked] = useState(false);

  const [searchParams] = useSearchParams();
  const clientId = parseId(searchParams.get('client'));
  const [allTags, setAllTags] = useState<any[] | undefined>(undefined);
  const needs = {
    scenario: `/instances/${instanceId}/scenarios/${scenarioId}`,
    groups: `/instances/${instanceId}/row_identifiers?type=Group`,
    defaultModelFiles: `/default_model_files/${instanceId}`,
    submissions: `/instances/${instanceId}/scenarios/${scenarioId}/submissions`,
    modelInstance: `/instances/${instanceId}`,
    availableTags: getScenarioLinkTagsListPath(clientId),
  };

  // Loader only fetches page 1 of the tag list (same cached path as before).
  // Whenever that cache entry settles, page it fully so tags past the first
  // page are not dropped; falls back to page 1 until then.
  useEffect(() => {
    const cache = scenarioDetailsContext as APICache;
    if (!cache || !clientId) return undefined;
    const path = getScenarioLinkTagsListPath(clientId);
    let active = true;
    const sync = (): void => {
      const { loading, data } = cache.summarize({ tags: path });
      if (loading || !data.tags) return;
      loadAllScenarioLinkTags(clientId, async () => data.tags)
        .then((items) => active && setAllTags(items))
        .catch(() => undefined);
    };
    const subscription = cache.subscribe([path], sync);
    sync();
    return () => {
      active = false;
      subscription.cancel();
    };
  }, [scenarioDetailsContext, clientId]);

  const refresh = (): Promise<any[]> => {
    return (scenarioDetailsContext as APICache).load(
      [
        `/instances/${instanceId}/scenarios/${scenarioId}/submissions`,
        `/instances/${instanceId}/scenarios/${scenarioId}`,
        `/instances/${instanceId}/row_identifiers?type=Group`,
        `/instances/${instanceId}/scenarios/${scenarioId}/submissions`,
      ],
      true
    );
  };

  const sendGroupsCountData = (data: any) => {
    const { totalGroupsCount, lockedGroupsCount } = data;
    if (
      totalGroupsCount &&
      lockedGroupsCount &&
      totalGroupsCount === lockedGroupsCount
    ) {
      setAllGroupsLocked(true);
    } else {
      setAllGroupsLocked(false);
    }
  };

  const renderContent = ({
    data,
  }: LoaderInfo<{
    scenario: Scenario;
    groups: RowIdentifier[];
    defaultModelFiles: DefaultModelFiles[];
    submissions: Submission[];
    modelInstance: ModelInstance;
    availableTags: { items: any[] };
  }>) => {
    const {
      scenario,
      groups,
      defaultModelFiles,
      submissions,
      modelInstance,
      availableTags,
    } = data;
    const availableScenarioLinkTags = allTags ?? availableTags?.items ?? [];

    let isActionRestricted = false;
    if (profile !== undefined) {
      isActionRestricted = isModelRestricted(profile, modelId);
    }

    let canSubmit = false;
    switch (scenario !== undefined && scenario.Edit_State) {
      case ScenarioEditState.Locked: {
        canSubmit = false;
        break;
      }
      case ScenarioEditState.OwnerEdit: {
        // OwnerEdit should be called "Trinity User or Model Owner"
        canSubmit =
          profile !== undefined &&
          (isTrinityUser(profile.User)
            ? isModelMember(profile, modelId)
            : isModelOwner(profile, modelId));
        break;
      }
      case ScenarioEditState.WorldEdit: {
        canSubmit = profile !== undefined && isModelMember(profile, modelId);
        break;
      }
      // no default
    }
    // Only allow submitting if scenario is unarchived
    if (scenario !== undefined) {
      canSubmit =
        canSubmit && scenario.Archive_Flag === ScenarioArchiveFlag.Current;
    }

    if (scenario === undefined) {
      return (
        <Box p={3}>
          <CenteredSpinner />
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 2, pb: 3 }}>
        <Box mb={2}>
          <ScenarioDetailsForm modelId={modelId} scenario={scenario} />
        </Box>
        <Card>
          <CardContent>
            {some(submissions, { Status: SubmissionStatus.Finished }) &&
              modelInstance?.IsCalculationEnabled && (
                <Box m={1} py={1}>
                  <EditAssumptions
                    disabled={!canSubmit}
                    allGroupsLocked={allGroupsLocked}
                  />
                </Box>
              )}
            <Box m={1} py={1}>
              <DownloadForecast
                instanceId={instanceId}
                scenarioId={scenarioId}
                lockSubmissionId={scenario.LockSubmissionID}
                refresh={refresh}
                doesInstanceHaveGroups={!every(groups, { GroupID: 0 })}
                defaultModelFiles={defaultModelFiles}
              />
            </Box>
            {scenario.Archive_Flag === ScenarioArchiveFlag.Current && (
              <Box m={1} py={1}>
                <UploadForecast
                  disabled={!canSubmit}
                  instanceId={instanceId}
                  scenarioId={scenarioId}
                  lockSubmissionId={scenario.LockSubmissionID}
                  refresh={refresh}
                  allGroupsLocked={allGroupsLocked}
                />
              </Box>
            )}
          </CardContent>
        </Card>
        <ScenarioTagAssignment
          scenario={scenario}
          availableTags={availableScenarioLinkTags}
          clientId={clientId}
          modelInstance={modelInstance}
        />
        <Box>
          <ScenarioVersions
            modelId={modelId}
            instanceId={instanceId}
            scenario={scenario}
            canSubmit={canSubmit}
            groups={data.groups}
            isActionRestricted={isActionRestricted}
            sendGroupsCount={sendGroupsCountData}
            refreshScenarios={refreshScenarios}
          />
        </Box>
      </Box>
    );
  };

  if (instanceId) {
    return (
      <Loader
        needs={needs}
        handleErrors={true}
        render={renderContent}
        force={true}
      />
    );
  }
  return null;
};

export default withProfile(ScenarioDetails);
